import * as React from "react";
import {
    BorderOptions,
    BurdeFontResponse,
    CalendarState,
    ImageResource,
} from "burde";
import { getBorderOptions } from "ui/service";
import { actionCreators } from "page/calendar/store";
import { actionCreators as burdeContentActionCreators } from "burde-api-content/store";
import { LayoutApiResponse, SvgCalendarLayout } from "ui/constants";
import { actionCreators as uiActionCreators } from "ui/store";
import LayoutPicker from "./layout-picker";
import * as cn from "classnames/bind";
import * as styles from "component/layout/common/controls-area/layout-selector/layout-selector.css";
import {
    calendarDayModuleCheck,
    calendarWeekModuleCheck,
} from "page/calendar/helper";
import { useEffect, useState } from "react";

const cx = cn.bind(styles);

const tabGroupCn = () =>
    cx({
        tabGroup: true,
    });

const tabGroupLiCn = () =>
    cx({
        tabGroupLi: true,
    });

const tabItemCn = (isSelected: boolean) =>
    cx({
        categoryTabItem: true,
        categoryTabItemSelected: isSelected,
    });

type LayoutSelectorProps = {
    calendar: CalendarState;
    userHasChangedBorder: boolean;
    borderOptions: BorderOptions | null;
    layoutOptions: LayoutApiResponse;
    isLoadingLayoutOptions: boolean;
    calendarLayout: SvgCalendarLayout | null;
    fontOptions: BurdeFontResponse | null;
} & typeof actionCreators &
    typeof burdeContentActionCreators &
    typeof uiActionCreators;

const LayoutSelector: React.FC<LayoutSelectorProps> = (props) => {
    const [selectedTab, setSelectedTab] = useState(0);

    const setCalendarLayout = (layout: SvgCalendarLayout | null) => {
        if (layout !== null && props.calendar.layoutId !== layout.id) {
            const { optionalDaySettings } = props.calendar;
            optionalDaySettings.showTimestamps = false;
            props.updateOptionalDaySettings(optionalDaySettings);
            props.updateSelectedLayout(layout);
            props.updateLayoutId(layout.id);
            layoutFontCheck(layout);
            layoutLineStyleCheck(layout);
            layoutBorderCheck(layout);
        }
    };

    // If the previously selected font set doesn't exist on the selected layout,
    // change it to the first font on the new layout
    const layoutFontCheck = (layout: SvgCalendarLayout) => {
        if (
            props.calendar.fontId &&
            props.fontOptions &&
            layout.fonts.length > 0
        ) {
            const foundCalendarFont = props.fontOptions.fontResources.find(
                (x) => x.id === props.calendar.fontId,
            );
            if (
                foundCalendarFont &&
                !layout.fonts.includes(foundCalendarFont.title)
            ) {
                const foundLayoutFont = props.fontOptions.fontResources.find(
                    (x) => x.title === layout.fonts[0],
                );
                if (foundLayoutFont) {
                    props.updateFontId(foundLayoutFont.id);
                }
            }
        }
    };

    // Same with line options
    const layoutLineStyleCheck = (layout: SvgCalendarLayout) => {
        if (
            layout.lines.length > 0 &&
            !layout.lines.includes(props.calendar.lines)
        ) {
            props.updateLines(layout.lines[0]);
        }
    };

    // If a layout has a frame or a color, apply it if no frame or color is selected already.
    const layoutBorderCheck = (layout: SvgCalendarLayout) => {
        if (!props.userHasChangedBorder && layout.defaultValues) {
            if (
                layout.defaultValues.borderImage !== null &&
                props.borderOptions !== null &&
                props.borderOptions.imageResources !== null
            ) {
                // Go through all border image categories
                for (const imageResource of props.borderOptions
                    .imageResources) {
                    let imageDetails = imageResource.images.find(
                        (y) => y.id == layout.defaultValues.borderImage,
                    );
                    if (imageDetails !== undefined) {
                        // Update border image
                        props.updateBorderImageId(imageDetails.id);
                    }
                }
            } else if (layout.defaultValues.borderColor !== null) {
                // Update border color
                props.updateBorderColor(layout.defaultValues.borderColor);
            }
        }
    };

    const onSetSelectedTab = (layout: SvgCalendarLayout) => {
        const { layoutOptions } = props;

        if (!layoutOptions) {
            setSelectedTab(0);
            return;
        }

        // Find selected tab
        if (layoutOptions.layoutResources.length > 0) {
            for (let i = 0; i < layoutOptions.layoutResources.length; i++) {
                let found = layoutOptions.layoutResources[i].layouts.find(
                    (svgLayout) => svgLayout.id == layout.id,
                );
                if (found !== undefined) {
                    // Update default layout
                    setSelectedTab(i);
                    break;
                }
            }
        }
    };

    useEffect(() => {
        const {
            calendarLayout,
            calendar,
            removeDayModule,
            removeWeekModule,
        } = props;

        if (calendarLayout !== null) {
            onSetSelectedTab(calendarLayout);
            layoutFontCheck(calendarLayout);
            layoutLineStyleCheck(calendarLayout);
            layoutBorderCheck(calendarLayout);
            // Remove day modules if there are more than what's allowed
            calendarDayModuleCheck(
                calendar.dayModules,
                calendarLayout,
                removeDayModule,
            );
            // Remove week modules if there are more than what's allowed
            calendarWeekModuleCheck(
                calendar.weekModules,
                calendarLayout,
                removeWeekModule,
            );
        }
    }, [props.calendarLayout]);

    const {
        calendarLayout,
        isLoadingLayoutOptions,
        layoutOptions,
        calendar,
    } = props;

    if (calendarLayout == null) {
        return <div></div>;
    } else {
        return (
            <div>
                {!isLoadingLayoutOptions &&
                layoutOptions !== null &&
                layoutOptions.layoutResources &&
                layoutOptions.layoutResources.length > 0 ? (
                    <div>
                        <ul className={tabGroupCn()}>
                            {layoutOptions.layoutResources.length > 1 &&
                                layoutOptions.layoutResources.map(
                                    (item, index) => (
                                        <li
                                            key={item.resourceGroupTitle}
                                            className={tabGroupLiCn()}>
                                            <a
                                                className={tabItemCn(
                                                    index === selectedTab,
                                                )}
                                                onClick={() =>
                                                    setSelectedTab(index)
                                                }>
                                                {item.resourceGroupTitle}
                                            </a>
                                        </li>
                                    ),
                                )}
                        </ul>
                        <LayoutPicker
                            onClick={setCalendarLayout}
                            selected={calendarLayout}
                            layouts={
                                layoutOptions.layoutResources[selectedTab]
                                    ? layoutOptions.layoutResources[selectedTab]
                                          .layouts
                                    : []
                            }
                            calendarType={calendar.calendarType}
                        />
                    </div>
                ) : (
                    <div />
                )}
            </div>
        );
    }
};

export default LayoutSelector;
